import { Button, useToast } from '@kanos/ui';
import { api, failure, ProblemAlert, saveDisclosure } from '@kanos/web-core';
import { useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';

/** Fetches what hangs on the own account and hands it to the browser as a file (ADR-034). */
const useOwnDisclosure = () =>
  useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/portal/disclosure');
      if (!data) throw failure('discloseOwnPortalData', error, response.status);
      saveDisclosure(data, 'eigene-angaben');
    },
  });

/**
 * The account's own disclosure (Art. 15, 20 GDPR, ADR-034): what it accepted and stated itself,
 * as a file. Everything else the firm holds - its checks, identity data - it compiles on request.
 */
export function OwnDisclosure({ firmName }: { firmName: string }) {
  const disclosure = useOwnDisclosure();
  const toast = useToast();
  return (
    <section className="grid gap-3 border-t border-hairline pt-6">
      <h2 className="text-xl font-semibold tracking-[-0.02em]">Ihre Daten bei der Kanzlei</h2>
      <p className="text-sm text-muted">
        Laden Sie herunter, was Sie hier angegeben haben – als Datei zum Lesen oder Mitnehmen (Art.
        15 und 20 DSGVO). Eine vollständige Auskunft, etwa mit den Ausweisdaten aus Ihrer
        Identifizierung, erhalten Sie auf Anfrage von {firmName}.
      </p>
      {disclosure.isError && <ProblemAlert error={disclosure.error} />}
      <Button
        className="justify-self-start"
        variant="secondary"
        isPending={disclosure.isPending}
        onPress={() =>
          disclosure.mutate(undefined, {
            onSuccess: () => toast({ tone: 'success', title: 'Datei erstellt' }),
          })
        }
      >
        <Download aria-hidden className="size-4" />
        Meine Angaben herunterladen
      </Button>
    </section>
  );
}
